/**
 * persistentFlag · localStorage with a strictly-necessary cookie fallback.
 *
 * Consent and dismissal flags MUST survive private browsing: there
 * `localStorage.setItem` throws, the choice evaporates, and every full page
 * load resurrects the banner the visitor already dismissed (founder repro
 * 2026-07-31: "one banner per page, couldn't make it go away"). A first-party
 * preference cookie is a strictly-necessary cookie under Law 25 / PIPEDA —
 * recording the consent decision itself never requires consent.
 *
 * The cookie NAME is the key with non-token characters replaced by "_"
 * (RFC 6265 cookie names cannot carry ":" — "cr:cookie-consent:v1" becomes
 * "cr_cookie-consent_v1"). localStorage keeps the original key.
 */

function cookieName(key: string): string {
  return key.replace(/[^a-zA-Z0-9_-]/g, "_");
}

export function readFlag(key: string): string | null {
  try {
    const v = window.localStorage.getItem(key);
    if (v != null) return v;
  } catch {
    // localStorage unavailable — fall through to the cookie.
  }
  try {
    const name = cookieName(key).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

export function writeFlag(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode — the cookie below is the one that persists.
  }
  try {
    document.cookie = `${cookieName(key)}=${encodeURIComponent(value)}; max-age=31536000; path=/; SameSite=Lax`;
  } catch {
    // Cookies blocked outright: the in-memory choice still holds for this page.
  }
}
